import { useMemo } from 'react'

import { readFragment } from '~publish/graphql'
import { CommentsPostCard_Post } from '~publish/pages/Community/fragments/CommentsPostCard_Post'

import type { PostListItem } from '../types'
import { usePostsListQuery } from './usePostsListQuery'
import type { UsePostsListQueryResult } from './usePostsListQuery'
import { usePostsWithCommentsQuery } from './usePostsWithCommentsQuery'

type UsePostsQueryOptions = {
  channelIds: string[]
  showOnlyWithComments: boolean
}

export type UsePostsQueryResult = UsePostsListQueryResult & {
  /** Posts with resolved fragment data for display. */
  resolvedPosts: PostListItem[]
  /** All resolved post IDs (stable reference). */
  postIds: string[]
}

/**
 * Unified hook that switches between the "all posts" and "posts with comments"
 * queries based on a toggle, and resolves fragment data for display.
 *
 * Consolidates the two-query pattern + fragment resolution that was previously
 * spread across CommentsByPostView.
 */
export function usePostsQuery({
  channelIds,
  showOnlyWithComments,
}: UsePostsQueryOptions): UsePostsQueryResult {
  const allPostsResult = usePostsListQuery({
    channelIds,
    status: ['sent'],
    skip: showOnlyWithComments,
  })

  const filteredPostsResult = usePostsWithCommentsQuery({
    channelIds,
    skip: !showOnlyWithComments,
  })

  const activeResult = showOnlyWithComments
    ? filteredPostsResult
    : allPostsResult

  const resolvedPosts = useMemo(
    () =>
      activeResult.posts.map((raw) => {
        const resolved = readFragment(CommentsPostCard_Post, raw)
        return { raw, resolved }
      }),
    [activeResult.posts],
  )

  const postIds = useMemo(
    () => resolvedPosts.map((p) => p.resolved.id),
    [resolvedPosts],
  )

  return useMemo(
    () => ({
      ...activeResult,
      resolvedPosts,
      postIds,
    }),
    [activeResult, resolvedPosts, postIds],
  )
}
